/**
 * Farm Recommendations Widget
 * Shows personalized farm recommendations based on location and preferences
 */

import React, { useState, useEffect } from 'react';
import { farmRecommendationAgent } from '@/services/agents';
import { Tractor, MapPin, Star, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FarmRecommendationsWidgetProps {
  currentFarmId?: string;
  userLocation?: { lat: number; lng: number };
  preferredCategories?: string[];
  allFarms: any[];
  maxRecommendations?: number;
}

const FarmRecommendationsWidget: React.FC<FarmRecommendationsWidgetProps> = ({
  currentFarmId,
  userLocation,
  preferredCategories,
  allFarms,
  maxRecommendations = 3,
}) => {
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getRecommendations = async () => {
      setLoading(true);

      try {
        let recs;

        if (currentFarmId) {
          // Get similar farms
          const currentFarm = allFarms.find((f) => f.id === currentFarmId);
          if (currentFarm) {
            const similarFarms = await farmRecommendationAgent.getSimilarFarms(
              currentFarm,
              allFarms,
              maxRecommendations
            );
            recs = similarFarms.map((farm) => ({
              farm,
              score: 0,
              reasons: ['Similar to current farm'],
            }));
          }
        } else if (userLocation || preferredCategories) {
          // Get personalized recommendations
          const preferences = {
            location: userLocation,
            preferredCategories: preferredCategories,
            maxDistance: 30,
          };

          recs = await farmRecommendationAgent.getRecommendations(
            allFarms,
            preferences,
            maxRecommendations
          );
        } else {
          // Get trending farms
          const trending = await farmRecommendationAgent.getTrendingFarms(
            allFarms,
            maxRecommendations
          );
          recs = trending.map((farm) => ({
            farm,
            score: 0,
            reasons: ['Popular in your area'],
          }));
        }

        setRecommendations(recs || []);
      } catch (error) {
        console.error('Error getting recommendations:', error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    if (allFarms && allFarms.length > 0) {
      getRecommendations();
    }
  }, [currentFarmId, userLocation, preferredCategories, allFarms, maxRecommendations]);

  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Tractor size={20} style={styles.icon} />
          <h3 style={styles.title}>Recommended For You</h3>
        </div>
        <p style={styles.loading}>Finding recommendations...</p>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <Tractor size={20} style={styles.icon} />
        <div>
          <h3 style={styles.title}>Recommended For You</h3>
          <p style={styles.subtitle}>Based on your preferences</p>
        </div>
      </div>

      <div style={styles.farmsList}>
        {recommendations.map((rec, index) => {
          const farm = rec.farm;
          return (
            <Link
              key={farm.id || index}
              to={`/listing/${farm.slug}`}
              style={styles.farmCard}
            >
              <div style={styles.farmCardContent}>
                <div style={styles.farmInfo}>
                  <h4 style={styles.farmName}>{farm.name}</h4>

                  {farm.location && (
                    <div style={styles.locationRow}>
                      <MapPin size={12} />
                      <span style={styles.locationText}>
                        {farm.city || farm.location.address}
                        {farm.distance && ` • ${farm.distance.toFixed(1)} mi`}
                      </span>
                    </div>
                  )}

                  {farm.rating && (
                    <div style={styles.ratingRow}>
                      <Star size={12} fill="#D4883A" color="#D4883A" />
                      <span style={styles.ratingText}>{farm.rating}</span>
                      {farm.reviewCount && (
                        <span style={styles.reviewCount}>({farm.reviewCount})</span>
                      )}
                    </div>
                  )}

                  {rec.reasons && rec.reasons.length > 0 && (
                    <div style={styles.reasons}>
                      {rec.reasons.slice(0, 2).map((reason: string, i: number) => (
                        <span key={i} style={styles.reasonBadge}>
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}

                  {farm.categories && farm.categories.length > 0 && (
                    <div style={styles.categories}>
                      {farm.categories.slice(0, 2).map((cat: string, i: number) => (
                        <span key={i} style={styles.categoryTag}>
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div style={styles.arrowIcon}>
                  <ArrowRight size={18} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div style={styles.footer}>
        <TrendingUp size={14} />
        <span style={styles.footerText}>Personalized based on your activity</span>
      </div>
    </div>
  );
};

const styles = {
  widget: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,
  icon: {
    color: '#2D5A27',
  } as React.CSSProperties,
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  } as React.CSSProperties,
  subtitle: {
    fontSize: '12px',
    color: '#999',
    margin: '4px 0 0 0',
  } as React.CSSProperties,
  loading: {
    fontSize: '14px',
    color: '#999',
    textAlign: 'center' as const,
    padding: '20px',
  } as React.CSSProperties,
  farmsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    marginBottom: '16px',
  } as React.CSSProperties,
  farmCard: {
    display: 'block',
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
    border: '1px solid #E8EDE7',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  } as React.CSSProperties,
  farmCardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  } as React.CSSProperties,
  farmInfo: {
    flex: 1,
  } as React.CSSProperties,
  farmName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '8px',
  } as React.CSSProperties,
  locationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
  } as React.CSSProperties,
  locationText: {
    fontSize: '12px',
    color: '#666',
  } as React.CSSProperties,
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '10px',
  } as React.CSSProperties,
  ratingText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1a1a1a',
  } as React.CSSProperties,
  reviewCount: {
    fontSize: '12px',
    color: '#999',
  } as React.CSSProperties,
  reasons: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    marginBottom: '8px',
  } as React.CSSProperties,
  reasonBadge: {
    fontSize: '10px',
    padding: '4px 8px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    borderRadius: '10px',
    fontWeight: '500',
  } as React.CSSProperties,
  categories: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
  } as React.CSSProperties,
  categoryTag: {
    fontSize: '11px',
    padding: '4px 10px',
    backgroundColor: '#E8EDE7',
    color: '#2D5A27',
    borderRadius: '12px',
    fontWeight: '500',
  } as React.CSSProperties,
  arrowIcon: {
    color: '#999',
    flexShrink: 0,
  } as React.CSSProperties,
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '12px',
    borderTop: '1px solid #E8EDE7',
  } as React.CSSProperties,
  footerText: {
    fontSize: '12px',
    color: '#999',
  } as React.CSSProperties,
};

export default FarmRecommendationsWidget;
